import { useState } from "react";
import { api } from "../../api/client";
import type { ScopedBench } from "../../api/types";
import { useAppState } from "../../state/appState";
import { useToasts } from "../../state/toasts";
import { Empty, Modal, Toggle } from "../../ui/components";
import { dateTime, money } from "../../ui/format";

export function BenchesPage() {
  const { snapshot } = useAppState();
  const { run, notify } = useToasts();

  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [renaming, setRenaming] = useState<ScopedBench | null>(null);
  const [newLabel, setNewLabel] = useState("");
  const [preview, setPreview] = useState<ScopedBench | null>(null);

  if (!snapshot) return <Empty>Загрузка данных…</Empty>;

  async function toggle(publicId: string, active: boolean) {
    await run(() => api.patch(`/api/admin/benches/${publicId}`, { status: active ? "active" : "disabled" }));
  }

  async function reissue(publicId: string) {
    const confirmed = confirm(
      `Перевыпустить код лавочки №${publicId}?\n\n` +
        "Все уже наклеенные QR-коды этой лавочки перестанут работать. " +
        "Если наклейка просто повреждена — перевыпуск не нужен, распечатайте её заново с тем же кодом."
    );
    if (!confirmed) return;

    await run(() => api.post(`/api/admin/benches/${publicId}/qr/reissue`), "Код перевыпущен");
  }

  async function create() {
    const ok = await run(() => api.post("/api/admin/benches", { label: label.trim() }), "Лавочка создана");
    if (ok) {
      setCreating(false);
      setLabel("");
    }
  }

  async function rename() {
    if (!renaming) return;
    const ok = await run(
      () => api.patch(`/api/admin/benches/${renaming.id}`, { label: newLabel.trim() }),
      "Лавочка переименована"
    );
    if (ok) setRenaming(null);
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      notify("Ссылка скопирована", "ok");
    } catch {
      notify("Браузер не дал доступ к буферу обмена", "error");
    }
  }

  const activeCount = snapshot.benches.filter(bench => bench.status === "active").length;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Точки доставки</p>
          <h1>Лавочки и QR</h1>
        </div>
        <div className="row-actions">
          <a className="btn btn-sm" href="/api/admin/benches/stickers.pdf" download>
            Печать всех наклеек ({activeCount})
          </a>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => setCreating(true)}>
            + Лавочка
          </button>
        </div>
      </div>

      <section className="panel">
        <header>
          <div>
            <h2>Справочник лавочек</h2>
            <p>Код печатается текстом под QR — по нему гость откроет меню, если наклейка повреждена.</p>
          </div>
        </header>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Лавочка</th>
                <th>Код наклейки</th>
                <th>Ссылка из QR</th>
                <th>Статистика</th>
                <th>Приём заказов</th>
                <th>Материалы</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.benches.map(bench => (
                <tr key={bench.id}>
                  <td className="num">
                    №{bench.label}
                    <br />
                    <small className="muted">{bench.status === "active" ? "активна" : "отключена"}</small>
                    <br />
                    <small className="muted">с {dateTime(bench.createdAt)}</small>
                  </td>
                  <td className="num">
                    <strong>{bench.qrCode}</strong>
                    <br />
                    <small className="muted">
                      {bench.qrReissuedAt
                        ? `перевыпущен ${dateTime(bench.qrReissuedAt)}`
                        : `выдан ${dateTime(bench.qrIssuedAt)}`}
                    </small>
                  </td>
                  <td>
                    <code className="tiny">{bench.qrUrl}</code>
                  </td>
                  <td className="num">
                    {bench.orders === undefined ? (
                      <span className="muted tiny">нет доступа</span>
                    ) : (
                      <>
                        {bench.orders} зак.
                        <br />
                        <small className="muted">{money(bench.revenue ?? 0)}</small>
                      </>
                    )}
                  </td>
                  <td>
                    <Toggle
                      checked={bench.status === "active"}
                      label={`Лавочка №${bench.label} принимает заказы`}
                      onChange={next => void toggle(bench.id, next)}
                    />
                  </td>
                  <td>
                    <div className="row-actions">
                      <button type="button" className="btn btn-sm" onClick={() => setPreview(bench)}>
                        Показать
                      </button>
                      <a className="btn btn-sm" href={`/api/admin/benches/${bench.id}/sticker.pdf`} download>
                        PDF
                      </a>
                      <a className="btn btn-sm" href={`/api/admin/benches/${bench.id}/sticker.svg`} download>
                        SVG
                      </a>
                      <a className="btn btn-sm" href={`/api/admin/benches/${bench.id}/qr.png`} download>
                        PNG
                      </a>
                    </div>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button type="button" className="btn btn-sm" onClick={() => void copy(bench.qrUrl)}>
                        Копировать
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => {
                          setRenaming(bench);
                          setNewLabel(bench.label);
                        }}
                      >
                        Переименовать
                      </button>
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => void reissue(bench.id)}>
                        Перевыпустить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="tiny muted">
          PNG содержит только сам код — он удобен для переписки. Печатать наклейки нужно из PDF или SVG: там есть номер
          лавочки и код текстом.
        </p>
      </section>

      {preview ? (
        <Modal
          title={`Наклейка лавочки №${preview.label}`}
          onClose={() => setPreview(null)}
          actions={
            <>
              <a className="btn" href={`/api/admin/benches/${preview.id}/sticker.pdf`} download>
                Скачать PDF
              </a>
              <button type="button" className="btn" onClick={() => setPreview(null)}>
                Закрыть
              </button>
            </>
          }
        >
          <img
            className="sticker-preview"
            src={`/api/admin/benches/${preview.id}/sticker.svg`}
            alt={`Наклейка лавочки №${preview.label} с кодом ${preview.qrCode}`}
          />
          <p className="tiny muted">
            Так выглядит наклейка. В печатной PDF-версии подписи только цифрами и латиницей: стандартные шрифты PDF не
            содержат кириллицы.
          </p>
        </Modal>
      ) : null}

      {creating ? (
        <Modal
          title="Новая лавочка"
          onClose={() => setCreating(false)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setCreating(false)}>
                Отмена
              </button>
              <button type="button" className="btn btn-primary" onClick={() => void create()} disabled={!label.trim()}>
                Создать
              </button>
            </>
          }
        >
          <label className="field">
            <span>Номер лавочки</span>
            <input autoFocus value={label} onChange={event => setLabel(event.target.value)} placeholder="21" />
          </label>
          <p className="tiny muted">Код наклейки будет выдан автоматически, его нельзя выбрать вручную.</p>
        </Modal>
      ) : null}

      {renaming ? (
        <Modal
          title={`Переименование лавочки №${renaming.label}`}
          onClose={() => setRenaming(null)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setRenaming(null)}>
                Отмена
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void rename()}
                disabled={!newLabel.trim()}
              >
                Сохранить
              </button>
            </>
          }
        >
          <label className="field">
            <span>Новый номер</span>
            <input autoFocus value={newLabel} onChange={event => setNewLabel(event.target.value)} />
          </label>
          <p className="tiny muted">
            Код наклейки не изменится — уже наклеенные QR продолжат работать. Но наклейку придётся перепечатать, потому
            что на ней напечатан старый номер.
          </p>
        </Modal>
      ) : null}
    </>
  );
}
